/**
 * FactCard Component
 * 
 * Displays a single fact for review with confirm/reject/edit actions.
 */

import React from 'react';
import { X, Check, Pencil, Loader } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import { getCategoryStyle } from './categoryStyles';

export default function FactCard({
  fact,
  isProcessing,
  isEditing,
  editContent,
  onEditContentChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onConfirm,
  onReject
}) {
  const categoryStyle = getCategoryStyle(fact.category);

  return (
    <div
      style={{
        padding: '16px 18px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        opacity: isProcessing ? 0.6 : 1,
        transition: 'all 0.2s ease'
      }}
    >
      {/* Category pill */}
      <span style={{
        display: 'inline-block',
        padding: '3px 10px',
        background: `${categoryStyle.color}15`,
        color: categoryStyle.color,
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600',
        marginBottom: '10px',
        textTransform: 'capitalize'
      }}>
        {categoryStyle.label}
      </span>

      {/* Fact content with edit icon */}
      {isEditing ? (
        <div>
          <textarea
            value={editContent}
            onChange={(e) => onEditContentChange(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${ABLE_COLORS.primary}`,
              borderRadius: '12px',
              fontSize: '15px',
              fontFamily: 'inherit',
              resize: 'vertical',
              minHeight: '80px',
              boxSizing: 'border-box',
              lineHeight: '1.5'
            }}
            autoFocus
          />
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginTop: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={onCancelEdit}
              style={{
                padding: '8px 16px',
                background: ABLE_COLORS.bgSubtle,
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                color: ABLE_COLORS.textSecondary,
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={onSaveEdit}
              disabled={!editContent.trim() || isProcessing}
              style={{
                padding: '8px 16px',
                background: ABLE_COLORS.primary,
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: editContent.trim() ? 'pointer' : 'not-allowed',
                color: 'white',
                fontWeight: '500',
                opacity: editContent.trim() ? 1 : 0.5
              }}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            marginBottom: '14px'
          }}>
            <p style={{
              margin: 0,
              fontSize: '15px',
              color: ABLE_COLORS.textPrimary,
              lineHeight: '1.5',
              flex: 1
            }}>
              {fact.content}
            </p>
            <button
              onClick={onStartEdit}
              disabled={isProcessing}
              title="Edit"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: ABLE_COLORS.textMuted,
                borderRadius: '6px',
                flexShrink: 0,
                opacity: 0.6,
                transition: 'opacity 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
            >
              <Pencil size={16} />
            </button>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onReject}
              disabled={isProcessing}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px 16px',
                background: 'white',
                border: `1.5px solid ${ABLE_COLORS.border}`,
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isProcessing ? 'wait' : 'pointer',
                color: ABLE_COLORS.textSecondary,
                transition: 'all 0.15s ease'
              }}
            >
              <X size={16} />
              Reject
            </button>
            <button
              onClick={onConfirm}
              disabled={isProcessing}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px 16px',
                background: ABLE_COLORS.primary,
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isProcessing ? 'wait' : 'pointer',
                color: 'white',
                transition: 'all 0.15s ease'
              }}
            >
              {isProcessing ? (
                <Loader size={16} className="spin" />
              ) : (
                <Check size={16} />
              )}
              Confirm
            </button>
          </div>
        </>
      )}
    </div>
  );
}
